export type ProfileCompletionItem = {
  label: string;
  filled: boolean;
};

const tutorProfileLabels = [
  "Dados básicos",
  "Endereço",
  "Foto de perfil",
  "Pets cadastrados",
] as const;

const anfitriaoFallbackLabels = [
  "Foto de perfil",
  "Bio",
  "Endereço",
  "Fotos do espaço",
  "Documentos",
  "PIX",
  "Serviços",
  "Vídeo acordo",
  "FAQ acordo",
  "Termos acordo",
] as const;

const hasValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return value !== null && value !== undefined;
};

const allHaveValue = (values: unknown[]): boolean => values.every(hasValue);

const getItemFilled = (items: unknown, label: string): boolean => {
  const source = Array.isArray(items) ? items : [];
  const item = source.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      "label" in candidate &&
      candidate.label === label,
  );

  return Boolean(
    item &&
      typeof item === "object" &&
      "filled" in item &&
      item.filled,
  );
};

export const normalizeTutorProfileItems = (
  items: unknown,
): ProfileCompletionItem[] =>
  tutorProfileLabels.map((label) => ({
    label,
    filled: getItemFilled(items, label),
  }));

export const normalizeAnfitriaoFallbackItems = (
  items: unknown,
): ProfileCompletionItem[] => {
  const confirmationsFilled = [
    "Vídeo acordo",
    "FAQ acordo",
    "Termos acordo",
  ].every((label) => getItemFilled(items, label));

  return [
    {
      label: "Foto de perfil",
      filled: getItemFilled(items, "Foto de perfil"),
    },
    {
      label: "Apresentação",
      filled: getItemFilled(items, "Bio"),
    },
    {
      label: "Endereço de atendimento",
      filled: getItemFilled(items, "Endereço"),
    },
    {
      label: "Fotos do atendimento",
      filled: getItemFilled(items, "Fotos do espaço"),
    },
    {
      label: "Serviços oferecidos",
      filled: getItemFilled(items, "Serviços"),
    },
    {
      label: "Chave PIX",
      filled: getItemFilled(items, "PIX"),
    },
    {
      label: "Confirmações finais",
      filled: confirmationsFilled,
    },
    {
      label: "Documentos de identificação",
      filled: getItemFilled(items, "Documentos"),
    },
  ];
};

// Mantido para documentar explicitamente quais rótulos legados podem ser usados
// no fallback. Campos históricos sem equivalente no formulário React são ignorados.
void anfitriaoFallbackLabels;

type HostProfileData = {
  host_profile?: {
    bio?: unknown;
    foto_perfil?: unknown;
    fotos_espaco?: unknown[];
    cep?: unknown;
    estado?: unknown;
    cidade?: unknown;
    bairro?: unknown;
    rua?: unknown;
    numero?: unknown;
    pix_chave?: unknown;
  };
  host_request?: {
    tipo_prestador?: unknown;
    hospedagem?: unknown;
    creche?: unknown;
    passeio?: unknown;
    petsitter?: unknown;
    taxi_dog?: unknown;
    adestramento?: unknown;
    vet_casa?: unknown;
    banho_tosa?: unknown;
  };
  documentos?: {
    documento_frente?: unknown;
    documento_verso?: unknown;
    selfie_documento?: unknown;
    comprovante_endereco?: unknown;
  };
  confirmacoes?: {
    aceita_termos?: unknown;
    aceita_politicas?: unknown;
    confirma_veracidade?: unknown;
  };
  mv?: {
    dados?: {
      crmv?: unknown;
      telefone?: unknown;
      email?: unknown;
    };
    modalidades?: Record<string, unknown>;
    especialidades?: unknown[];
    documentos?: {
      doc_foto?: unknown;
      doc_crmv?: unknown;
      doc_endereco_prof?: unknown;
    };
  } | null;
};

const isChecked = (value: unknown): boolean =>
  value === true || value === 1 || value === "1";

export const buildAnfitriaoProfileItems = (
  data: HostProfileData | null | undefined,
): ProfileCompletionItem[] => {
  const profile = data?.host_profile;
  const request = data?.host_request;
  const documents = data?.documentos;
  const confirmations = data?.confirmacoes;
  const tipoPrestador = request?.tipo_prestador;
  const isMv = tipoPrestador === "medico_veterinario";

  const temEndereco = allHaveValue([
    profile?.cep,
    profile?.estado,
    profile?.cidade,
    profile?.bairro,
    profile?.rua,
    profile?.numero,
  ]);

  const temFotoAtendimento = Array.isArray(profile?.fotos_espaco)
    ? profile.fotos_espaco.some(hasValue)
    : false;

  const temServico = [
    request?.hospedagem,
    request?.creche,
    request?.passeio,
    request?.petsitter,
    request?.taxi_dog,
    request?.adestramento,
    request?.vet_casa,
    request?.banho_tosa,
  ].some(isChecked);

  const confirmacoesCompletas = [
    confirmations?.aceita_termos,
    confirmations?.aceita_politicas,
    confirmations?.confirma_veracidade,
  ].every(isChecked);

  const commonItems: ProfileCompletionItem[] = [
    { label: "Foto de perfil", filled: hasValue(profile?.foto_perfil) },
    { label: "Apresentação", filled: hasValue(profile?.bio) },
    { label: "Endereço de atendimento", filled: temEndereco },
    { label: "Fotos do atendimento", filled: temFotoAtendimento },
    { label: "Serviços oferecidos", filled: temServico },
    { label: "Chave PIX", filled: hasValue(profile?.pix_chave) },
    { label: "Confirmações finais", filled: confirmacoesCompletas },
  ];

  if (!isMv) {
    return [
      ...commonItems,
      {
        label: "Documentos de identificação",
        filled: allHaveValue([
          documents?.documento_frente,
          documents?.documento_verso,
          documents?.selfie_documento,
          documents?.comprovante_endereco,
        ]),
      },
    ];
  }

  const mv = data?.mv;
  const modalidades = mv?.modalidades ? Object.values(mv.modalidades) : [];

  return [
    ...commonItems,
    {
      label: "Dados profissionais",
      filled: allHaveValue([
        mv?.dados?.crmv,
        mv?.dados?.telefone,
        mv?.dados?.email,
      ]),
    },
    {
      label: "Modalidades de atendimento",
      filled: modalidades.some(isChecked),
    },
    {
      label: "Especialidades",
      filled:
        Array.isArray(mv?.especialidades) && mv.especialidades.length > 0,
    },
    {
      label: "Documentos profissionais",
      filled: allHaveValue([
        mv?.documentos?.doc_foto,
        mv?.documentos?.doc_crmv,
        mv?.documentos?.doc_endereco_prof,
      ]),
    },
  ];
};

export const calculateProfilePercent = (
  items: ProfileCompletionItem[],
): number => {
  if (items.length === 0) {
    return 0;
  }

  const filled = items.filter((item) => item.filled).length;
  return Math.round((filled / items.length) * 100);
};